/**
 * Accompagnements AFRICTAL — Module Super Admin (additif)
 * ─────────────────────────────────────────────────────────────────────────
 * Centralise TOUTES les demandes complexes transmises par l'Assistant
 * AFRICTAL AVANT toute intervention de l'Orchestrateur.
 *
 * Ce module est ADDITIF — il n'écrase ni ne remplace aucune page existante.
 * Il utilise UNIQUEMENT les APIs déjà exposées :
 *   • GET  /api/assistant/escalations                          (toutes les demandes)
 *   • GET  /api/africtal-orchestrator/pending                  (pending enrichi)
 *   • POST /api/africtal-orchestrator/{id}/authorize-ai        (autorisation IA)
 *   • POST /api/africtal-orchestrator/{id}/redirect-expert     (réorientation expert)
 *
 * L'Orchestrateur ne démarre JAMAIS tant que l'administrateur n'a pas
 * explicitement cliqué sur « Autoriser l'Orchestrateur ». C'est le principe
 * même du backend actuel — ce module en devient le point d'entrée unique et
 * exhaustif côté Super Admin.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Sparkles, ShieldCheck, User as UserIcon, Clock, ArrowUpRight,
  Loader2, MessageSquare, XCircle, CheckCircle2, Filter, Search,
  AlertTriangle, RefreshCw, Mail, Phone, Send,
} from "lucide-react";
import { toast } from "sonner";
import PrivateLayout from "../components/PrivateLayout";
import { api, formatApiError } from "../services/api";

const STATUS_META = {
  pending:    { label: "En attente d'autorisation", color: "#F4B400", bg: "rgba(244,180,0,0.12)" },
  authorized: { label: "Orchestrateur autorisé",    color: "#009640", bg: "rgba(0,150,64,0.12)"  },
  redirected: { label: "Réorienté vers expert",     color: "#3b82f6", bg: "rgba(59,130,246,0.12)" },
  resolved:   { label: "Traité",                     color: "#6b7480", bg: "rgba(107,116,128,0.14)" },
};

const PRIORITY_META = {
  high:   { label: "Prioritaire", color: "#c02a2a" },
  normal: { label: "Standard",     color: "#009640" },
  low:    { label: "Basse",        color: "#6b7480" },
};

const ROLE_LABEL = {
  admin: "Administrateur", talent: "Talent", employeur: "Employeur",
  freelance: "Freelance", entrepreneur: "Entrepreneur", investisseur: "Investisseur",
  universite: "Université", coach: "Coach", "super-admin": "Super Admin",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}


export default function AdminAccompagnements() {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter]       = useState("pending"); // pending|all|authorized|redirected|resolved
  const [query, setQuery]         = useState("");
  const [selected, setSelected]   = useState(null);      // escalation currently opened in the drawer
  const [action, setAction]       = useState(null);      // "authorize" | "redirect" | null
  const [priority, setPriority]   = useState("normal");
  const [contact, setContact]     = useState("email");
  const [note, setNote]           = useState("");
  const [busy, setBusy]           = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/assistant/escalations");
      setItems(data.items || []);
    } catch (e) {
      console.warn("[admin-accomp] load failed:", formatApiError(e));
      toast.error("Impossible de charger les accompagnements.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const s = { total: items.length, pending: 0, authorized: 0, redirected: 0, resolved: 0 };
    for (const it of items) {
      const st = it.status || "pending";
      if (s[st] !== undefined) s[st] += 1;
    }
    return s;
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      const st = it.status || "pending";
      if (filter !== "all" && st !== filter) return false;
      if (!q) return true;
      return (
        (it.message || "").toLowerCase().includes(q) ||
        (it.user_email || "").toLowerCase().includes(q) ||
        (it.id || "").toLowerCase().includes(q)
      );
    });
  }, [items, filter, query]);

  const openAction = (esc, act) => {
    setSelected(esc);
    setAction(act);
    setPriority("normal");
    setContact("email");
    setNote("");
  };

  const closeDrawer = () => {
    if (busy) return;
    setSelected(null);
    setAction(null);
    setNote("");
  };

  const submit = async () => {
    if (!selected || !action) return;
    setBusy(true);
    try {
      const url = action === "authorize"
        ? `/africtal-orchestrator/${selected.id}/authorize-ai`
        : `/africtal-orchestrator/${selected.id}/redirect-expert`;
      const body = action === "authorize"
        ? { priority, admin_note: note }
        : { reason: note, contact_channel: contact };
      await api.post(url, body);
      toast.success(
        action === "authorize"
          ? "Orchestrateur autorisé — l'accompagnement personnalisé démarre."
          : "Demande réorientée vers un expert humain."
      );
      closeDrawer();
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Action impossible pour l'instant.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <PrivateLayout
      title="Accompagnements AFRICTAL"
      breadcrumbs={[{ label: "Super Admin" }, { label: "Accompagnements" }]}
      actions={
        <button
          type="button"
          onClick={load}
          className="afr-btn afr-btn--outline afr-btn--sm"
          data-testid="admin-accompagnements-refresh"
          disabled={refreshing}
        >
          {refreshing ? <Loader2 size={14} className="afr-spin" /> : <RefreshCw size={14} />}
          Rafraîchir
        </button>
      }
    >
      {/* Intro / Statement */}
      <div
        className="afr-card"
        data-testid="admin-accompagnements-intro"
        style={{
          padding: "1.2rem 1.4rem",
          background: "linear-gradient(135deg, color-mix(in oklab, var(--afr-primary) 8%, var(--afr-surface)), var(--afr-surface))",
          borderColor: "color-mix(in oklab, var(--afr-primary) 25%, var(--afr-border))",
          marginBottom: "1.4rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem" }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "var(--afr-primary)", color: "#fff", display: "grid", placeItems: "center" }}>
            <ShieldCheck size={22} />
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: "0 0 0.3rem", fontFamily: "'Outfit',sans-serif", color: "var(--afr-secondary)", fontSize: "1.05rem" }}>
              Point d'entrée unique du contrôle des accompagnements IA
            </h3>
            <p className="afr-muted" style={{ margin: 0, lineHeight: 1.55, fontSize: "0.9rem" }}>
              Toutes les demandes complexes transmises par l'Assistant AFRICTAL sont centralisées ici.
              L'<strong>Orchestrateur ne démarre jamais</strong> tant que vous n'avez pas autorisé la demande.
              Vous pouvez « Autoriser l'Orchestrateur » ou « Réorienter vers un expert humain ».
            </p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="afr-shell__kpis" data-testid="admin-accompagnements-kpis" style={{ marginBottom: "1.2rem" }}>
        <StatCard label="Total" value={stats.total} color="var(--afr-secondary)" icon={Sparkles} testId="kpi-total" />
        <StatCard label="En attente"          value={stats.pending}    color={STATUS_META.pending.color}    icon={Clock}         testId="kpi-pending" />
        <StatCard label="Orchestrateur"       value={stats.authorized} color={STATUS_META.authorized.color} icon={CheckCircle2}  testId="kpi-authorized" />
        <StatCard label="Expert humain"       value={stats.redirected} color={STATUS_META.redirected.color} icon={UserIcon}      testId="kpi-redirected" />
        <StatCard label="Traité"              value={stats.resolved}   color={STATUS_META.resolved.color}   icon={ShieldCheck}   testId="kpi-resolved" />
      </div>

      {/* Filters bar */}
      <div
        className="afr-card"
        data-testid="admin-accompagnements-filters"
        style={{ padding: "0.9rem 1rem", marginBottom: "1rem", display: "flex", gap: "0.8rem", alignItems: "center", flexWrap: "wrap" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", color: "var(--afr-muted)" }}>
          <Filter size={16} /> Filtrer :
        </div>
        {["pending", "authorized", "redirected", "resolved", "all"].map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`afr-btn afr-btn--sm ${filter === f ? "afr-btn--primary" : "afr-btn--ghost"}`}
            data-testid={`filter-${f}`}
          >
            {f === "all" ? "Toutes" : STATUS_META[f]?.label || f}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <div style={{ position: "relative", minWidth: 240 }}>
          <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--afr-muted)" }} />
          <input
            type="text"
            placeholder="Rechercher (message, email, id)…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="admin-accompagnements-search"
            style={{
              width: "100%", padding: "0.55rem 0.6rem 0.55rem 2rem",
              borderRadius: 10, border: "1px solid var(--afr-border)",
              background: "var(--afr-surface)", color: "var(--afr-secondary)",
              fontFamily: "'Outfit',sans-serif", fontSize: "0.9rem",
            }}
          />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="afr-card" style={{ padding: "3rem", display: "grid", placeItems: "center" }} data-testid="admin-accompagnements-loading">
          <Loader2 size={22} className="afr-spin" style={{ color: "var(--afr-primary)" }} />
          <span className="afr-muted" style={{ marginTop: "0.6rem" }}>Chargement des accompagnements…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="afr-card" style={{ padding: "2.2rem", textAlign: "center" }} data-testid="admin-accompagnements-empty">
          <Sparkles size={26} style={{ color: "var(--afr-primary)", marginBottom: "0.6rem" }} />
          <h4 style={{ margin: "0 0 0.4rem", fontFamily: "'Outfit',sans-serif", color: "var(--afr-secondary)" }}>
            Aucune demande à afficher
          </h4>
          <p className="afr-muted" style={{ margin: 0 }}>
            {filter === "pending"
              ? "Aucune nouvelle demande d'accompagnement pour l'instant."
              : "Aucun accompagnement ne correspond à ce filtre."}
          </p>
        </div>
      ) : (
        <div className="afr-grid" style={{ gridTemplateColumns: "1fr", gap: "0.9rem" }} data-testid="admin-accompagnements-list">
          {filtered.map((esc) => (
            <EscalationRow
              key={esc.id}
              esc={esc}
              onAuthorize={() => openAction(esc, "authorize")}
              onRedirect={() => openAction(esc, "redirect")}
              onOpen={() => openAction(esc, null)}
              busy={busy && selected?.id === esc.id}
            />
          ))}
        </div>
      )}

      {/* Detail drawer / action modal */}
      {selected && (
        <DetailDrawer
          esc={selected}
          action={action}
          setAction={setAction}
          priority={priority}
          setPriority={setPriority}
          contact={contact}
          setContact={setContact}
          note={note}
          setNote={setNote}
          onClose={closeDrawer}
          onSubmit={submit}
          busy={busy}
        />
      )}
    </PrivateLayout>
  );
}


function StatCard({ label, value, color, icon: Icon, testId }) {
  return (
    <div
      className="afr-kpi"
      data-testid={testId}
      style={{ border: "1px solid var(--afr-border)", background: "var(--afr-surface)" }}
    >
      <div className="afr-kpi__icon" style={{ background: `color-mix(in oklab, ${color} 14%, transparent)`, color }}>
        <Icon size={18} />
      </div>
      <span className="afr-kpi__label">{label}</span>
      <span className="afr-kpi__value">{value}</span>
    </div>
  );
}


function EscalationRow({ esc, onAuthorize, onRedirect, onOpen, busy }) {
  const st = esc.status || "pending";
  const meta = STATUS_META[st] || STATUS_META.pending;
  const canAct = st === "pending";
  const priority = esc.priority || "normal";
  const priorityMeta = PRIORITY_META[priority] || PRIORITY_META.normal;

  return (
    <article
      className="afr-card"
      data-testid={`accompagnement-row-${esc.id}`}
      style={{ padding: "1.1rem 1.2rem" }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center", marginBottom: "0.55rem" }}>
            <span
              style={{ background: meta.bg, color: meta.color, padding: "0.22rem 0.6rem", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700, fontFamily: "'Outfit',sans-serif" }}
              data-testid={`accompagnement-status-${esc.id}`}
            >
              {meta.label}
            </span>
            {st === "authorized" && (
              <span
                style={{ background: "rgba(107,116,128,0.12)", color: priorityMeta.color, padding: "0.22rem 0.6rem", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700 }}
              >
                Priorité : {priorityMeta.label}
              </span>
            )}
            <span className="afr-muted" style={{ fontSize: "0.78rem", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
              <Clock size={12} /> {fmtDate(esc.created_at)}
            </span>
          </div>

          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "0.5rem", color: "var(--afr-secondary)", fontFamily: "'Outfit',sans-serif", fontSize: "0.92rem" }}>
            <UserIcon size={14} />
            <strong>
              {esc.user_snapshot?.name || esc.user_email || "Visiteur anonyme"}
            </strong>
            {esc.user_snapshot?.role && (
              <span className="afr-muted" style={{ fontSize: "0.82rem" }}>
                · {ROLE_LABEL[esc.user_snapshot.role] || esc.user_snapshot.role}
              </span>
            )}
            {esc.user_email && (
              <span className="afr-muted" style={{ fontSize: "0.82rem" }}>· {esc.user_email}</span>
            )}
          </div>

          <p style={{
            margin: 0,
            fontFamily: "'Outfit',sans-serif",
            color: "var(--afr-secondary)",
            lineHeight: 1.55,
            fontSize: "0.94rem",
          }}>
            {(esc.message || "").length > 280 ? `${(esc.message || "").slice(0, 280)}…` : esc.message}
          </p>
          {esc.resolution_note && (
            <p className="afr-muted" style={{ fontSize: "0.82rem", marginTop: "0.6rem", fontStyle: "italic" }}>
              Note admin : {esc.resolution_note}
            </p>
          )}
        </div>

        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "flex-start" }}>
          <button
            type="button"
            className="afr-btn afr-btn--outline afr-btn--sm"
            onClick={onOpen}
            data-testid={`accompagnement-open-${esc.id}`}
          >
            Détails
          </button>
          {canAct && (
            <>
              <button
                type="button"
                className="afr-btn afr-btn--primary afr-btn--sm"
                onClick={onAuthorize}
                disabled={busy}
                data-testid={`accompagnement-authorize-${esc.id}`}
              >
                <Sparkles size={14} /> Autoriser l'Orchestrateur
              </button>
              <button
                type="button"
                className="afr-btn afr-btn--outline afr-btn--sm"
                onClick={onRedirect}
                disabled={busy}
                data-testid={`accompagnement-redirect-${esc.id}`}
              >
                <UserIcon size={14} /> Réorienter vers un expert
              </button>
            </>
          )}
        </div>
      </div>
    </article>
  );
}


function DetailDrawer({
  esc, action, setAction,
  priority, setPriority,
  contact, setContact,
  note, setNote,
  onClose, onSubmit, busy,
}) {
  const canAct = (esc.status || "pending") === "pending";
  const submitLabel = action === "authorize"
    ? (busy ? "Autorisation…" : "Autoriser l'Orchestrateur")
    : (busy ? "Réorientation…" : "Réorienter vers un expert");

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="accompagnement-drawer"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,42,66,0.55)",
        display: "grid", placeItems: "center", zIndex: 200, padding: "1rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="afr-card"
        style={{
          background: "var(--afr-surface)",
          padding: "1.4rem 1.6rem",
          borderRadius: 18,
          maxWidth: 640,
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 24px 60px rgba(15,42,66,0.35)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
          <h3 style={{ margin: 0, fontFamily: "'Outfit',sans-serif", color: "var(--afr-secondary)", fontSize: "1.15rem" }}>
            Accompagnement · {esc.id.slice(0, 8)}
          </h3>
          <button
            type="button"
            aria-label="Fermer"
            onClick={onClose}
            data-testid="accompagnement-drawer-close"
            disabled={busy}
            className="afr-topbar__icon"
            style={{ background: "transparent" }}
          >
            <XCircle size={20} />
          </button>
        </div>

        {/* Metadata */}
        <div style={{ display: "grid", gap: "0.4rem", marginBottom: "1rem", fontSize: "0.88rem" }}>
          <MetaRow label="Statut">
            <span
              style={{
                background: STATUS_META[esc.status || "pending"].bg,
                color: STATUS_META[esc.status || "pending"].color,
                padding: "0.18rem 0.5rem", borderRadius: 999, fontWeight: 700, fontSize: "0.78rem",
              }}
              data-testid="drawer-status"
            >
              {STATUS_META[esc.status || "pending"].label}
            </span>
          </MetaRow>
          <MetaRow label="Utilisateur">
            {esc.user_snapshot?.name || esc.user_email || "Visiteur anonyme"}
            {esc.user_snapshot?.role && <span className="afr-muted"> · {ROLE_LABEL[esc.user_snapshot.role] || esc.user_snapshot.role}</span>}
          </MetaRow>
          {esc.user_email && <MetaRow label="Email">{esc.user_email}</MetaRow>}
          <MetaRow label="Reçue le">{fmtDate(esc.created_at)}</MetaRow>
          {esc.resolved_at && <MetaRow label="Traitée le">{fmtDate(esc.resolved_at)}</MetaRow>}
        </div>

        {/* Full message */}
        <div style={{ marginBottom: "1rem" }}>
          <label className="afr-muted" style={{ fontSize: "0.78rem", display: "block", marginBottom: "0.3rem" }}>
            Demande initiale
          </label>
          <div
            data-testid="drawer-message"
            style={{
              background: "color-mix(in oklab, var(--afr-primary) 4%, var(--afr-surface))",
              border: "1px solid var(--afr-border)",
              borderRadius: 12,
              padding: "0.9rem 1rem",
              color: "var(--afr-secondary)",
              lineHeight: 1.6,
              fontSize: "0.94rem",
              whiteSpace: "pre-wrap",
            }}
          >
            {esc.message || "—"}
          </div>
        </div>

        {esc.resolution_note && (
          <div style={{ marginBottom: "1rem" }}>
            <label className="afr-muted" style={{ fontSize: "0.78rem", display: "block", marginBottom: "0.3rem" }}>
              Note administrative
            </label>
            <div style={{ padding: "0.7rem 0.9rem", background: "rgba(107,116,128,0.08)", borderRadius: 10, fontSize: "0.9rem", color: "var(--afr-secondary)" }}>
              {esc.resolution_note}
            </div>
          </div>
        )}

        {/* Action panel */}
        {canAct && (
          <div style={{ borderTop: "1px solid var(--afr-border)", paddingTop: "1rem", marginTop: "0.6rem" }}>
            {/* Action switcher */}
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.9rem", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => setAction("authorize")}
                className={`afr-btn afr-btn--sm ${action === "authorize" ? "afr-btn--primary" : "afr-btn--ghost"}`}
                data-testid="drawer-tab-authorize"
              >
                <Sparkles size={14} /> Autoriser l'Orchestrateur
              </button>
              <button
                type="button"
                onClick={() => setAction("redirect")}
                className={`afr-btn afr-btn--sm ${action === "redirect" ? "afr-btn--primary" : "afr-btn--ghost"}`}
                data-testid="drawer-tab-redirect"
              >
                <UserIcon size={14} /> Réorienter vers expert
              </button>
            </div>

            {action === "authorize" && (
              <>
                <div style={{ marginBottom: "0.8rem" }}>
                  <label className="afr-muted" style={{ fontSize: "0.78rem", display: "block", marginBottom: "0.3rem" }}>
                    Priorité de l'accompagnement
                  </label>
                  <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                    {Object.entries(PRIORITY_META).map(([p, m]) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setPriority(p)}
                        className={`afr-btn afr-btn--sm ${priority === p ? "afr-btn--primary" : "afr-btn--ghost"}`}
                        data-testid={`drawer-priority-${p}`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="afr-muted" style={{ fontSize: "0.78rem", display: "block", marginBottom: "0.3rem" }}>
                  Note interne pour l'Orchestrateur (optionnelle)
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Consignes, priorités métier, informations utiles…"
                  rows={3}
                  data-testid="drawer-note-authorize"
                  style={{
                    width: "100%", padding: "0.6rem", borderRadius: 10,
                    border: "1px solid var(--afr-border)", background: "var(--afr-surface)",
                    fontFamily: "'Outfit',sans-serif", fontSize: "0.9rem", color: "var(--afr-secondary)",
                    resize: "vertical",
                  }}
                />
              </>
            )}

            {action === "redirect" && (
              <>
                <div style={{ marginBottom: "0.8rem" }}>
                  <label className="afr-muted" style={{ fontSize: "0.78rem", display: "block", marginBottom: "0.3rem" }}>
                    Canal de reprise de contact
                  </label>
                  <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                    {[
                      { v: "email", label: "Email", Icon: Mail },
                      { v: "phone", label: "Téléphone", Icon: Phone },
                      { v: "in_app", label: "Messagerie AFRICTAL", Icon: MessageSquare },
                    ].map(({ v, label, Icon }) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setContact(v)}
                        className={`afr-btn afr-btn--sm ${contact === v ? "afr-btn--primary" : "afr-btn--ghost"}`}
                        data-testid={`drawer-contact-${v}`}
                      >
                        <Icon size={14} /> {label}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="afr-muted" style={{ fontSize: "0.78rem", display: "block", marginBottom: "0.3rem" }}>
                  Raison de la réorientation (optionnelle)
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Pourquoi cette demande mérite un expert humain plutôt que l'Orchestrateur ?"
                  rows={3}
                  data-testid="drawer-note-redirect"
                  style={{
                    width: "100%", padding: "0.6rem", borderRadius: 10,
                    border: "1px solid var(--afr-border)", background: "var(--afr-surface)",
                    fontFamily: "'Outfit',sans-serif", fontSize: "0.9rem", color: "var(--afr-secondary)",
                    resize: "vertical",
                  }}
                />
              </>
            )}

            {action && (
              <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", marginTop: "1rem" }}>
                <button
                  type="button"
                  className="afr-btn afr-btn--outline afr-btn--sm"
                  onClick={onClose}
                  disabled={busy}
                  data-testid="drawer-cancel"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="afr-btn afr-btn--primary afr-btn--sm"
                  onClick={onSubmit}
                  disabled={busy}
                  data-testid="drawer-submit"
                >
                  {busy ? <Loader2 size={14} className="afr-spin" /> : <Send size={14} />}
                  {submitLabel}
                </button>
              </div>
            )}

            {!action && (
              <div className="afr-muted" style={{ fontSize: "0.85rem", background: "rgba(244,180,0,0.1)", padding: "0.7rem 0.9rem", borderRadius: 10, display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
                <AlertTriangle size={16} style={{ color: "#F4B400", marginTop: 2 }} />
                <span>Choisissez une action ci-dessus. L'Orchestrateur ne peut pas démarrer tant qu'aucune décision n'a été prise.</span>
              </div>
            )}
          </div>
        )}

        {!canAct && (
          <div className="afr-muted" style={{ fontSize: "0.85rem", padding: "0.8rem 1rem", background: "rgba(0,150,64,0.08)", borderRadius: 10, display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <CheckCircle2 size={16} style={{ color: "#009640" }} />
            Cette demande a déjà été traitée.
          </div>
        )}
      </div>
    </div>
  );
}


function MetaRow({ label, children }) {
  return (
    <div style={{ display: "flex", gap: "0.6rem", alignItems: "baseline" }}>
      <span className="afr-muted" style={{ minWidth: 90, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.4px" }}>
        {label}
      </span>
      <span style={{ color: "var(--afr-secondary)", fontFamily: "'Outfit',sans-serif", fontSize: "0.92rem" }}>
        {children}
      </span>
    </div>
  );
}
